import React, { useEffect, useCallback, useMemo } from 'react';

import { useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import Grid from '@mui/material/Grid';

import Page from 'components/Page';
import PaperBox from 'components/paper/PaperBox';
import ButtonForm from 'components/forms/ButtonForm';

import useGlobalState from 'actions';
import services from 'services';
import utils from 'utils';

import juloLogo from 'assets/logo_julo.png';
import {
  juloHeader,
  summaryHeading,
  textLeft,
  textRight,
  txtSummary,
  txtSummaryLeft,
  txtSummaryRight,
} from './styles';
import { centerAlignOfferDiv } from 'pages/Home/HomePage/styles';

const AgreementSummaryPage = () => {
  const [globalState, globalActions] = useGlobalState();
  const history = useHistory();
  const itemList = useMemo(() => [], []);

  const showErrorNotification = useCallback(
    (msg) => {
      globalActions.openNotification(msg);
      setTimeout(() => {
        globalActions.closeNotification();
      }, globalState.notificationDelayTime);
    },
    [globalActions, globalState.notificationDelayTime],
  );

  const getAgreementSummary = useCallback(() => {
    services.ga.sendEventsToGA('Page', 'Display', 'id_signdetails_default');
    let summaryData = localStorage.getItem('summaryData');
    if (summaryData) {
      summaryData = JSON.parse(summaryData);
      const loanXid = localStorage.getItem('loan_xid');
      if (loanXid === '' || loanXid === null) {
        services.loan.getApplicationStatus(
          (response) => {
            if (response.data) {
              if (
                response.data?.application &&
                response.data?.application?.application_status === 190
              ) {
                localStorage.setItem(
                  'appStatus',
                  response.data.application.application_status,
                );
                if (response.data?.loan && response.data?.loan?.status >= 210) {
                  localStorage.setItem('loan_xid', response.data.loan.loan_xid);
                } else {
                  showErrorNotification(globalState.noLoanXidError);
                }
              } else {
                showErrorNotification(globalState.noLoanXidError);
              }
            } else {
              showErrorNotification(globalState.generalErrorMsg);
            }
          },
          (error) => {
            showErrorNotification(globalActions.getResponseError(error));
          },
        );
      }
      if (summaryData.loan && summaryData.bank) {
        const amount = globalActions.thousands_separators(
          summaryData.loan.amount,
        );
        const upfrontFee = globalActions.thousands_separators(
          summaryData.loan.upfront_fee,
        );
        const disbursalAmount = globalActions.thousands_separators(
          summaryData.loan.disbursal_amount,
        );
        const dailyRepayment = globalActions.thousands_separators(
          summaryData.loan.daily_repayment,
        );
        const repayIn = summaryData.loan.tenure;
        const interestRate = summaryData.loan.interest_rate;

        itemList.push(
          <div key={0} className={juloHeader}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm container>
                <Grid item xs container direction='column' spacing={2}>
                  <Grid item xs>
                    <div>
                      <img src={juloLogo} alt='logo' />
                    </div>
                  </Grid>
                </Grid>
                <Grid item>
                  <div className={summaryHeading}>Ringkasan Perjanjian</div>
                </Grid>
              </Grid>
            </Grid>
          </div>,
        );
        itemList.push(
          <div key={1} style={{ width: '100%' }}>
            <PaperBox
              elevation={2}
              style={{ width: '100%', marginTop: '24px' }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm container>
                  <Grid item xs container direction='column' spacing={2}>
                    <Grid item xs>
                      <div className={txtSummaryLeft}>Jumlah pinjaman</div>
                      <div className={txtSummaryLeft}>Biaya admin</div>
                      <div className={txtSummaryLeft}>Dana cair</div>
                      <div className={txtSummaryLeft}>Cicilan per hari</div>
                      <div className={txtSummaryLeft}>Jangka waktu</div>
                      <div className={txtSummaryLeft}>Bunga per bulan</div>
                    </Grid>
                  </Grid>
                  <Grid item>
                    <div className={txtSummaryRight}>Rp {amount}</div>
                    <div className={txtSummaryRight}>Rp {upfrontFee}</div>
                    <div className={txtSummaryRight}>Rp {disbursalAmount}</div>
                    <div className={txtSummaryRight}>Rp {dailyRepayment}</div>
                    <div className={txtSummaryRight}>{repayIn} days</div>
                    <div className={txtSummaryRight}>{interestRate}%</div>
                  </Grid>
                </Grid>
              </Grid>
            </PaperBox>
          </div>,
        );
        itemList.push(
          <div key={2} style={{ width: '100%' }} className={txtSummary}>
            Recipient&apos;s Bank Account
          </div>,
        );
        itemList.push(
          <div key={3} style={{ width: '100%' }}>
            <PaperBox
              elevation={2}
              style={{ width: '100%', marginTop: '16px' }}
            >
              <Grid container direction='column'>
                <Grid item container justify='space-between'>
                  <span className={textLeft}>Nama bank</span>
                  <span className={textRight}>
                    {summaryData.bank.bank_name}
                  </span>
                </Grid>
                <Grid item container justify='space-between'>
                  <span className={textLeft}>Atas nama</span>
                  <span className={textRight}>
                    {summaryData.bank.account_name}
                  </span>
                </Grid>
                <Grid item container justify='space-between'>
                  <span className={textLeft}>Nomor Rekening</span>
                  <span className={textRight}>
                    {summaryData.bank.account_number}
                  </span>
                </Grid>
              </Grid>
            </PaperBox>
          </div>,
        );
        globalActions.setState('summaryData', itemList);
      } else {
        history.goBack();
      }
    } else {
      history.goBack();
    }
  }, [
    globalActions,
    globalState.generalErrorMsg,
    globalState.noLoanXidError,
    history,
    itemList,
    showErrorNotification,
  ]);

  useEffect(() => {
    const hasToken = utils.store.get('token');
    if (!hasToken) {
      history.push('/login');
    } else {
      getAgreementSummary();
    }
  }, [getAgreementSummary, history]);

  return (
    <>
      <Helmet>
        <title>JULO - Ringkasan Perjanjian</title>
      </Helmet>
      <Page useHeader={true}>
        <div className={centerAlignOfferDiv}>
          {globalState.summaryData}
          <div className='grab-footer'>
            <div style={{ padding: '16px 0' }}>
              <ButtonForm
                onClick={() => {
                  localStorage.setItem('redirectPage', '/agreement_summary');
                  history.push('/agreement_letter');
                }}
              >
                Lihat SKRTP
              </ButtonForm>
            </div>
          </div>
        </div>
      </Page>
    </>
  );
};

export default AgreementSummaryPage;
